import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { Users, Plus, Trash2, Phone, Mail, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const countries = [
  { code: "KE", name: "Kenya", flag: "🇰🇪" },
  { code: "UG", name: "Uganda", flag: "🇺🇬" },
  { code: "TZ", name: "Tanzania", flag: "🇹🇿" },
  { code: "RW", name: "Rwanda", flag: "🇷🇼" },
];

const accountTypes = [
  { value: "mobile_money", label: "Mobile Money" },
  { value: "bank", label: "Bank Account" },
  { value: "onepay", label: "OnePay User" },
];

export default function RecipientsPage() {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [newRecipient, setNewRecipient] = useState({
    name: "",
    phone: "",
    email: "",
    country: "KE",
    account_type: "mobile_money",
  });

  const fetchRecipients = async () => {
    try {
      const response = await axios.get(`${API}/recipients`, { withCredentials: true });
      setRecipients(response.data);
    } catch (error) {
      console.error("Failed to fetch recipients:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipients();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newRecipient.name) {
      toast.error("Please enter recipient name");
      return;
    }
    if (!newRecipient.phone && !newRecipient.email) {
      toast.error("Please enter phone or email");
      return;
    }

    setCreateLoading(true);
    try {
      await axios.post(`${API}/recipients`, newRecipient, { withCredentials: true });
      toast.success("Recipient added successfully!");
      setNewRecipient({
        name: "",
        phone: "",
        email: "",
        country: "KE",
        account_type: "mobile_money",
      });
      setDialogOpen(false);
      fetchRecipients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add recipient");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async (recipientId) => {
    setDeleteLoading(recipientId);
    try {
      await axios.delete(`${API}/recipients/${recipientId}`, { withCredentials: true });
      toast.success("Recipient deleted");
      fetchRecipients();
    } catch (error) {
      toast.error("Failed to delete recipient");
    } finally {
      setDeleteLoading(null);
    }
  };

  const getCountryInfo = (code) => countries.find((c) => c.code === code);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#14B8A6] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="recipients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Recipients</h1>
            <p className="text-slate-600 mt-1">Manage your saved recipients</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488]" data-testid="add-recipient-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Recipient
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Recipient</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    placeholder="John Doe"
                    value={newRecipient.name}
                    onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                    className="h-12"
                    data-testid="recipient-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      placeholder="+254 7XX XXX XXX"
                      value={newRecipient.phone}
                      onChange={(e) => setNewRecipient({ ...newRecipient, phone: e.target.value })}
                      className="h-12"
                      data-testid="recipient-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={newRecipient.email}
                      onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                      className="h-12"
                      data-testid="recipient-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Select
                      value={newRecipient.country}
                      onValueChange={(value) => setNewRecipient({ ...newRecipient, country: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="recipient-country-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.flag} {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Account Type</Label>
                    <Select
                      value={newRecipient.account_type}
                      onValueChange={(value) => setNewRecipient({ ...newRecipient, account_type: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="recipient-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {accountTypes.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                  disabled={createLoading}
                  data-testid="save-recipient-btn"
                >
                  {createLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Add Recipient
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Recipients List */}
        <Card className="border-slate-200" data-testid="recipients-list">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              {recipients.length} Recipients
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recipients.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recipients.map((r) => (
                  <div
                    key={r.recipient_id}
                    className="p-4 bg-slate-50 rounded-xl"
                    data-testid={`recipient-${r.recipient_id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-[#14B8A6] flex items-center justify-center text-white text-lg font-bold">
                          {r.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{r.name}</p>
                          <p className="text-xs text-slate-500">
                            {getCountryInfo(r.country)?.flag} {getCountryInfo(r.country)?.name}
                          </p>
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-600"
                            data-testid={`delete-recipient-${r.recipient_id}`}
                          >
                            {deleteLoading === r.recipient_id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Recipient?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove {r.name} from your recipients. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(r.recipient_id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    <div className="space-y-2">
                      {r.phone && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Phone className="h-4 w-4" />
                          {r.phone}
                        </div>
                      )}
                      {r.email && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="h-4 w-4" />
                          {r.email}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p className="mb-4">No recipients yet</p>
                <Button
                  className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488]"
                  onClick={() => setDialogOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Recipient
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
